import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import Layout from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { Plus, Calendar, Users, DollarSign, MessageSquare, Check, X, Eye, Loader2, Star, Ban } from 'lucide-react';
import ReviewModal from '../components/ReviewModal';

const HostDashboard = () => {
  const navigate = useNavigate();
  const { api, user, token } = useAuth();
  const { t, formatCurrency, formatDate } = useLanguage();

  const [weddings, setWeddings] = useState([]);
  const [applications, setApplications] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedWedding, setSelectedWedding] = useState(null);
  const [processingApp, setProcessingApp] = useState(null);
  const [hostReviews, setHostReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [selectedReviewWedding, setSelectedReviewWedding] = useState(null);
  const [selectedReviewedUser, setSelectedReviewedUser] = useState(null);
  const [cancellingWedding, setCancellingWedding] = useState(null);

  const fetchWeddings = useCallback(async () => {
    try {
      const response = await api.get('/weddings/host/me');
      setWeddings(response.data);
      
      // Fetch applications for each wedding
      for (const wedding of response.data) {
        const appsResponse = await api.get(`/applications/wedding/${wedding.id}`);
        setApplications((prev) => ({ ...prev, [wedding.id]: appsResponse.data }));
      }
    } catch (error) {
      console.error('Failed to fetch weddings:', error);
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  }, [api, t]);

  const fetchHostReviews = useCallback(async () => {
    try {
      setReviewsLoading(true);
      const response = await api.get(`/api/reviews/user/${user.id}`);
      setHostReviews(response.data);
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
    } finally {
      setReviewsLoading(false);
    }
  }, [api, user]);

  useEffect(() => {
    fetchWeddings();
    fetchHostReviews();
  }, [fetchWeddings, fetchHostReviews]);

  const handleApplicationStatus = async (appId, status) => {
    setProcessingApp(appId);
    try {
      await api.put(`/applications/${appId}/status?status=${status}`);
      toast.success(`Application ${status}`);
      fetchWeddings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update application');
    } finally {
      setProcessingApp(null);
    }
  };

  const handleCancelWedding = async (wedding) => {
    const paidApps = (applications[wedding.id] || []).filter((app) => app.payment_status === 'paid').length;
    const warning = paidApps > 0
      ? `This will cancel "${wedding.title}" and automatically refund ${paidApps} paid guest(s). This cannot be undone. Continue?`
      : `Cancel "${wedding.title}"? This cannot be undone.`;
    if (!window.confirm(warning)) return;

    setCancellingWedding(wedding.id);
    try {
      await api.post(`/weddings/${wedding.id}/cancel`);
      toast.success('Wedding cancelled');
      fetchWeddings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel wedding');
    } finally {
      setCancellingWedding(null);
    }
  };

  const totalEarnings = weddings.reduce((sum, wedding) => {
    const weddingApps = applications[wedding.id] || [];
    const paidApps = weddingApps.filter((app) => app.payment_status === 'paid');
    return sum + paidApps.length * wedding.price_per_guest * 0.85; // After 15% platform fee
  }, 0);

  const totalApplications = Object.values(applications).flat().length;
  const pendingApplications = Object.values(applications)
    .flat()
    .filter((app) => app.status === 'pending').length;

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-secondary py-8">
          <div className="max-w-7xl mx-auto px-4">
            <Skeleton className="h-10 w-48 mb-8" />
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              {[...Array(4)].map((_, i) => (
                <Skeleton key={i} className="h-32" />
              ))}
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-secondary" data-testid="host-dashboard">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
            <div>
              <h1 className="font-serif text-3xl font-bold">{t('dashboard.host.title')}</h1>
              <p className="text-muted-foreground">Welcome back, {user?.name}</p>
            </div>
            <Button
              onClick={() => navigate('/host/weddings/create')}
              className="mt-4 md:mt-0 rounded-full"
              data-testid="create-wedding-btn"
            >
              <Plus className="mr-2 h-4 w-4" />
              {t('dashboard.host.createWedding')}
            </Button>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{t('dashboard.host.myWeddings')}</p>
                    <p className="text-3xl font-bold">{weddings.length}</p>
                  </div>
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                    <Calendar className="h-6 w-6 text-primary" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{t('dashboard.host.applications')}</p>
                    <p className="text-3xl font-bold">{totalApplications}</p>
                    {pendingApplications > 0 && (
                      <Badge variant="secondary" className="mt-1">
                        {pendingApplications} pending
                      </Badge>
                    )}
                  </div>
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                    <Users className="h-6 w-6 text-primary" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{t('dashboard.host.earnings')}</p>
                    <p className="text-3xl font-bold">{formatCurrency(totalEarnings, user?.currency_preference || 'EUR')}</p>
                  </div>
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                    <DollarSign className="h-6 w-6 text-primary" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{t('dashboard.host.messages')}</p>
                    <Button variant="link" className="p-0 h-auto" onClick={() => navigate('/messages')}>
                      View Messages
                    </Button>
                  </div>
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                    <MessageSquare className="h-6 w-6 text-primary" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Weddings & Applications Tabs */}
          <Tabs defaultValue="weddings">
            <TabsList className="mb-6">
              <TabsTrigger value="weddings" data-testid="tab-weddings">
                {t('dashboard.host.myWeddings')}
              </TabsTrigger>
              <TabsTrigger value="applications" data-testid="tab-applications">
                {t('dashboard.host.applications')}
              </TabsTrigger>
              <TabsTrigger value="reviews" data-testid="tab-reviews">
                {t('dashboard.host.reviews') || 'Reviews'}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="weddings">
              {weddings.length === 0 ? (
                <Card className="p-12 text-center">
                  <p className="text-muted-foreground mb-4">{t('dashboard.host.noWeddings')}</p>
                  <Button onClick={() => navigate('/host/weddings/create')} className="rounded-full">
                    <Plus className="mr-2 h-4 w-4" />
                    {t('dashboard.host.createWedding')}
                  </Button>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {weddings.map((wedding) => (
                    <Card key={wedding.id} className="overflow-hidden">
                      <div className="aspect-video bg-muted relative">
                        <img
                          src={wedding.images?.[0] || "https://images.pexels.com/photos/33485955/pexels-photo-33485955.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"}
                          alt={wedding.title}
                          className="w-full h-full object-cover"
                        />
                        <Badge
                          className={`absolute top-2 right-2 ${
                            wedding.status === 'open' ? 'bg-green-500' :
                            wedding.status === 'cancelled' ? 'bg-red-500' : 'bg-muted'
                          }`}
                        >
                          {wedding.status}
                        </Badge>
                        {wedding.verification_status === 'pending' && (
                          <Badge
                            className="absolute top-2 left-2 bg-yellow-500 text-white"
                          >
                            {t('wedding.verification.pending')}
                          </Badge>
                        )}
                        {wedding.verification_status === 'verified' && (
                          <Badge
                            className="absolute top-2 left-2 bg-green-500 text-white"
                          >
                            {t('wedding.verification.verified')}
                          </Badge>
                        )}
                        {wedding.verification_status === 'rejected' && (
                          <Badge
                            className="absolute top-2 left-2 bg-red-500 text-white"
                          >
                            {t('wedding.verification.rejected')}
                          </Badge>
                        )}
                        {wedding.is_featured && (
                          <Badge className="absolute top-2 left-2 bg-primary text-white">
                            Featured
                          </Badge>
                        )}
                      </div>
                      <CardContent className="p-4">
                        <h3 className="font-semibold mb-2">{wedding.title}</h3>
                        <p className="text-sm text-muted-foreground mb-2">
                          {formatDate(wedding.date)} • {wedding.location}
                        </p>
                        <div className="flex justify-between items-center">
                          <span className="text-sm">
                            {wedding.available_slots}/{wedding.total_slots} slots
                          </span>
                          <span className="font-semibold text-primary">
                            {formatCurrency(wedding.price_per_guest, wedding.currency)}
                          </span>
                        </div>
                        <div className="flex gap-2 mt-4">
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1"
                            onClick={() => setSelectedWedding(wedding)}
                            data-testid={`view-apps-${wedding.id}`}
                          >
                            <Eye className="mr-1 h-4 w-4" />
                            Applications ({applications[wedding.id]?.length || 0})
                          </Button>
                          {wedding.verification_status === 'rejected' && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1 text-red-600"
                              onClick={() => navigate(`/host/weddings/${wedding.id}/verification`)}
                              data-testid={`reupload-verification-${wedding.id}`}
                            >
                              {t('wedding.verification.reUpload')}
                            </Button>
                          )}
                          {!['cancelled', 'completed'].includes(wedding.status) && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-red-600"
                              onClick={() => handleCancelWedding(wedding)}
                              disabled={cancellingWedding === wedding.id}
                              data-testid={`cancel-wedding-${wedding.id}`}
                            >
                              {cancellingWedding === wedding.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Ban className="h-4 w-4" />
                              )}
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="applications">
              <Card>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Guest</TableHead>
                      <TableHead>Wedding</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Payment</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {Object.entries(applications).flatMap(([weddingId, apps]) =>
                      apps.map((app) => {
                        const wedding = weddings.find((w) => w.id === weddingId);
                        return (
                          <TableRow key={app.id} data-testid={`app-row-${app.id}`}>
                            <TableCell>
                              <div>
                                <p className="font-medium">{app.guest_name}</p>
                                <p className="text-sm text-muted-foreground">{app.guest_email}</p>
                              </div>
                            </TableCell>
                            <TableCell>{wedding?.title}</TableCell>
                            <TableCell>
                              <Badge
                                variant={
                                  app.status === 'approved' ? 'default' :
                                  app.status === 'rejected' ? 'destructive' : 'secondary'
                                }
                              >
                                {t(`application.status.${app.status}`)}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge variant={app.payment_status === 'paid' ? 'default' : 'outline'}>
                                {app.payment_status}
                              </Badge>
                            </TableCell>
                            <TableCell>{formatDate(app.created_at)}</TableCell>
                            <TableCell>
                              {app.status === 'pending' && (
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-green-600"
                                    onClick={() => handleApplicationStatus(app.id, 'approved')}
                                    disabled={processingApp === app.id}
                                    data-testid={`approve-${app.id}`}
                                  >
                                    {processingApp === app.id ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                      <Check className="h-4 w-4" />
                                    )}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-red-600"
                                    onClick={() => handleApplicationStatus(app.id, 'rejected')}
                                    disabled={processingApp === app.id}
                                    data-testid={`reject-${app.id}`}
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                </div>
                              )}
                              {app.status === 'approved' && app.payment_status === 'paid' && (
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => navigate(`/messages/${weddingId}/${app.guest_id}`)}
                                  >
                                    <MessageSquare className="h-4 w-4" />
                                  </Button>
                                  {wedding?.status === 'completed' && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => {
                                        setSelectedReviewWedding(wedding);
                                        setSelectedReviewedUser({
                                          id: app.guest_id,
                                          name: app.guest_name,
                                        });
                                      }}
                                      data-testid={`review-guest-${app.id}`}
                                    >
                                      <Star className="h-4 w-4" />
                                    </Button>
                                  )}
                                </div>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </Card>
            </TabsContent>

            <TabsContent value="reviews">
              {reviewsLoading ? (
                <Card className="p-12 text-center">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
                  <p className="text-muted-foreground mt-4">Loading reviews...</p>
                </Card>
              ) : hostReviews.length === 0 ? (
                <Card className="p-12 text-center">
                  <p className="text-muted-foreground mb-4">{t('review.noReviews') || 'No reviews yet'}</p>
                </Card>
              ) : (
                <div className="space-y-4">
                  {hostReviews.map((review) => {
                    const wedding = weddings.find((w) => w.id === review.wedding_id);
                    return (
                      <Card key={review.id}>
                        <CardContent className="p-4">
                          <div className="flex items-start gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="font-medium">{review.reviewer_name}</span>
                                <Badge variant="secondary">{review.rating}/5</Badge>
                              </div>
                              <div className="flex gap-1 mb-2">
                                {[1, 2, 3, 4, 5].map((star) => (
                                  <Star
                                    key={star}
                                    className={`h-5 w-5 ${
                                      star <= review.rating
                                        ? 'fill-yellow-400 text-yellow-400'
                                        : 'text-gray-300'
                                    }`}
                                  />
                                ))}
                              </div>
                              {review.comment && (
                                <p className="text-sm text-muted-foreground italic">"{review.comment}"</p>
                              )}
                              <p className="text-xs text-muted-foreground mt-1">
                                {wedding ? wedding.title : 'Unknown Wedding'} • {formatDate(review.created_at)}
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </TabsContent>
          </Tabs>

          {/* Application Detail Dialog */}
          <Dialog open={!!selectedWedding} onOpenChange={() => setSelectedWedding(null)}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle className="font-serif">
                  Applications for {selectedWedding?.title}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 max-h-[60vh] overflow-auto">
                {selectedWedding && applications[selectedWedding.id]?.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No applications yet</p>
                ) : (
                  selectedWedding && applications[selectedWedding.id]?.map((app) => (
                    <Card key={app.id}>
                      <CardContent className="p-4">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium">{app.guest_name}</p>
                            <p className="text-sm text-muted-foreground">{app.guest_email}</p>
                            {app.message && (
                              <p className="mt-2 text-sm bg-muted p-2 rounded">"{app.message}"</p>
                            )}
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            <Badge
                              variant={
                                app.status === 'approved' ? 'default' :
                                app.status === 'rejected' ? 'destructive' : 'secondary'
                              }
                            >
                              {app.status}
                            </Badge>
                            {app.status === 'pending' && (
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  onClick={() => handleApplicationStatus(app.id, 'approved')}
                                  disabled={processingApp === app.id}
                                >
                                  {processingApp === app.id ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Check className="mr-1 h-4 w-4" />
                                  )}
                                  Approve
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleApplicationStatus(app.id, 'rejected')}
                                  disabled={processingApp === app.id}
                                >
                                  <X className="mr-1 h-4 w-4" />
                                  Reject
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </DialogContent>
          </Dialog>
          <ReviewModal
            isOpen={!!selectedReviewWedding}
            onClose={() => {
              setSelectedReviewWedding(null);
              setSelectedReviewedUser(null);
            }}
            wedding={selectedReviewWedding}
            reviewedUser={selectedReviewedUser}
            token={token}
            onSuccess={() => {
              setSelectedReviewWedding(null);
              setSelectedReviewedUser(null);
            }}
          />
        </div>
      </div>
    </Layout>
  );
};

export default HostDashboard;
